// ============================================================================
// PÁGINA: NOTÍCIAS - PORTAL PÚBLICO
// ============================================================================
// Arquivo: src/pages/Noticias.tsx
// UPDATED: 2026-03-26 — ajuste visual no hero / remoção da faixa branca
// ============================================================================

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Search,
  Filter,
  Calendar,
  Tag,
  ChevronDown,
  X,
  Clock,
  Eye,
  Heart,
  TrendingUp,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { API_BASE } from '../utils/apiConfig';


type CategoriaNoticia =
  | "announcement"
  | "event"
  | "tutorial"
  | "industry_news"
  | "company_news"
  | "member_spotlight";

interface Noticia {
  id: string;
  title: string;
  subtitle?: string;
  summary: string;
  content: string;
  slug: string;
  category: CategoriaNoticia;
  featuredImageUrl?: string;
  publishedAt: string;
  viewsCount: number;
  likesCount: number;
  metadata?: {
    readingTime?: number;
  };
  author?: {
    name: string;
  };
  tags?: string[];
  isFeatured?: boolean;
}

const categoriasMap: Record<CategoriaNoticia, string> = {
  announcement: "Comunicado",
  event: "Evento",
  tutorial: "Tutorial",
  industry_news: "Notícia",
  company_news: "Empresa",
  member_spotlight: "Destaque",
};

const categoriasCores: Record<
  CategoriaNoticia,
  { bg: string; text: string; border: string }
> = {
  announcement: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    border: "border-blue-200",
  },
  event: {
    bg: "bg-purple-50",
    text: "text-purple-700",
    border: "border-purple-200",
  },
  tutorial: {
    bg: "bg-green-50",
    text: "text-green-700",
    border: "border-green-200",
  },
  industry_news: {
    bg: "bg-orange-50",
    text: "text-orange-700",
    border: "border-orange-200",
  },
  company_news: {
    bg: "bg-indigo-50",
    text: "text-indigo-700",
    border: "border-indigo-200",
  },
  member_spotlight: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
  },
};

const formatarDataBR = (iso: string) => {
  const date = new Date(iso);
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const Noticias: React.FC = () => {
  const [noticias, setNoticias] = useState<Noticia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busca, setBusca] = useState("");
  const [categoria, setCategoria] = useState<CategoriaNoticia | "">("");
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [pagina, setPagina] = useState(1);
  const porPagina = 9;

  useEffect(() => {
    const fetchNoticias = async () => {
      setLoading(true);
      setError(null);

      try {
        let url = `${API_BASE}/news?page=1&limit=100`;

        if (busca.trim().length >= 2) {
          url = `${API_BASE}/news/search?query=${encodeURIComponent(busca)}&limit=100`;
        }

        const res = await fetch(url);

        if (!res.ok) {
          throw new Error("Erro ao carregar notícias");
        }

        const contentType = res.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) {
          throw new Error("Resposta da API inválida");
        }

        const data = await res.json();
        setNoticias(data.news ?? data.data ?? []);
      } catch (err: any) {
        setError(err?.message ?? "Erro ao carregar notícias");
        setNoticias([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNoticias();
  }, [busca]);

  const noticiasFiltradas = useMemo(() => {
    return noticias.filter((n) => {
      if (categoria && n.category !== categoria) return false;
      return true;
    });
  }, [noticias, categoria]);

  const totalPaginas = Math.max(1, Math.ceil(noticiasFiltradas.length / porPagina));
  const paginaAtual = Math.min(pagina, totalPaginas);

  const noticiasPaginadas = noticiasFiltradas.slice(
    (paginaAtual - 1) * porPagina,
    paginaAtual * porPagina
  );

  const destaque = noticias.find((n) => n.isFeatured) ?? null;

  useEffect(() => {
    setPagina(1);
  }, [categoria]);

  return (
    <div className="min-h-screen bg-[#f8f6f2]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b0010] via-[#3a0d00] to-[#7a3100]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(251,191,36,0.10),transparent_40%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(255,120,0,0.10),transparent_35%)]" />

        <div className="container mx-auto px-6 relative z-10 py-24 md:py-28 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/15 mb-8 shadow-lg">
            <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-sm font-bold text-amber-100 uppercase tracking-wider">
              Central de Notícias
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl font-black text-white mb-4 tracking-tight">
            Notícias e Eventos
          </h1>

          <p className="mt-4 text-lg md:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Fique por dentro das últimas atualizações, eventos e comunicados oficiais
          </p>
        </div>
      </section>

      {/* DESTAQUE */}
      {destaque ? (
        <section className="relative -mt-6 md:-mt-8 z-10 pb-8 pt-10">
          <div className="container mx-auto px-6 max-w-7xl">
            <div className="bg-white rounded-3xl border border-amber-100 shadow-[0_20px_60px_rgba(0,0,0,0.08)] overflow-hidden transition-all duration-300">
              <div className="md:flex">
                <div className="md:w-5/12 bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center p-8">
                  {destaque.featuredImageUrl ? (
                    <div className="relative w-48 h-48 rounded-full overflow-hidden border-8 border-white shadow-2xl">
                      <img
                        src={destaque.featuredImageUrl}
                        alt={destaque.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-48 h-48 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center border-8 border-white shadow-2xl">
                      <TrendingUp className="w-24 h-24 text-white" />
                    </div>
                  )}
                </div>

                <div className="md:w-7/12 p-8 md:p-12">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold uppercase tracking-wider mb-6 shadow-lg">
                    <TrendingUp className="w-4 h-4" />
                    Em Destaque
                  </div>

                  <div
                    className={`inline-block px-4 py-1.5 rounded-full text-xs font-bold mb-4 border ${categoriasCores[destaque.category].bg} ${categoriasCores[destaque.category].text} ${categoriasCores[destaque.category].border}`}
                  >
                    {categoriasMap[destaque.category]}
                  </div>

                  <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
                    {destaque.title}
                  </h2>

                  {destaque.subtitle && (
                    <p className="text-xl text-gray-700 font-semibold mb-4">
                      {destaque.subtitle}
                    </p>
                  )}

                  <p className="text-gray-600 mb-6 leading-relaxed text-lg">
                    {destaque.summary}
                  </p>

                  <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-200">
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-amber-600" />
                      {formatarDataBR(destaque.publishedAt)}
                    </span>

                    {destaque.metadata?.readingTime && (
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-600" />
                        {destaque.metadata.readingTime} min de leitura
                      </span>
                    )}

                    <span className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-amber-600" />
                      {destaque.viewsCount.toLocaleString()} visualizações
                    </span>
                  </div>

                  <Link
                    to={`/publicacoes/noticias/${destaque.slug}`}
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-xl hover:shadow-lg transition-all duration-300 group"
                  >
                    Ler notícia completa
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <div className="h-8" />
      )}

      {/* BUSCA E FILTROS */}
      <section className="pb-10 pt-4">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex gap-4 mb-8">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                value={busca}
                onChange={(e) => {
                  setBusca(e.target.value);
                  setPagina(1);
                }}
                placeholder="Buscar notícias..."
                className="w-full pl-12 pr-4 py-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent shadow-sm"
              />
            </div>

            <button
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
              className="px-6 py-4 bg-[#0f172a] text-white font-bold rounded-xl flex items-center gap-2 hover:bg-[#111827] transition-all shadow-md"
            >
              <Filter className="w-5 h-5" />
              Filtros
              <ChevronDown
                className={`w-5 h-5 transition-transform ${
                  mostrarFiltros ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>

          {mostrarFiltros && (
            <div className="bg-white rounded-2xl border border-amber-100 p-6 mb-8 shadow-lg">
              <label className="block text-sm font-bold text-gray-700 mb-3">
                Categoria
              </label>
              <select
                value={categoria}
                onChange={(e) => {
                  setCategoria(e.target.value as CategoriaNoticia | "");
                  setPagina(1);
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl font-semibold text-gray-700 focus:ring-2 focus:ring-amber-500"
              >
                <option value="">Todas as categorias</option>
                {Object.entries(categoriasMap).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>

              <button
                onClick={() => {
                  setBusca("");
                  setCategoria("");
                  setPagina(1);
                }}
                className="mt-4 flex items-center gap-2 text-sm font-bold text-gray-600 hover:text-gray-900 transition-colors"
              >
                <X className="w-4 h-4" />
                Limpar filtros
              </button>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-12 h-12 text-amber-600 animate-spin" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-center gap-4">
              <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
              <div>
                <p className="font-bold text-red-900">Erro ao carregar notícias</p>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && noticiasPaginadas.length > 0 && (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {noticiasPaginadas.map((n) => {
                  const catConfig = categoriasCores[n.category];

                  return (
                    <article
                      key={n.id}
                      className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
                    >
                     <div className="h-48 overflow-hidden flex-shrink-0">
                        {n.featuredImageUrl ? (
                          <img
                            src={n.featuredImageUrl}
                            alt={n.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                            <Tag className="w-12 h-12 text-amber-400" />
                          </div>
                        )}
                      </div>
                      <div className="p-7">
                        <div
                          className={`inline-block px-3 py-1.5 rounded-full text-xs font-bold mb-3 border ${catConfig.bg} ${catConfig.text} ${catConfig.border}`}
                        >
                          {categoriasMap[n.category]}
                        </div>

                        <h3 className="text-xl font-black text-gray-900 mb-3 line-clamp-2 leading-tight group-hover:text-amber-700 transition-colors">
                          {n.title}
                        </h3>

                        <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                          {n.summary}
                        </p>

                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4 pb-4 border-b border-gray-200">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-4 h-4 text-amber-600" />
                            {formatarDataBR(n.publishedAt)}
                          </span>

                          {n.metadata?.readingTime && (
                            <span className="flex items-center gap-1.5">
                              <Clock className="w-4 h-4 text-amber-600" />
                              {n.metadata.readingTime} min
                            </span>
                          )}
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1.5">
                              <Eye className="w-4 h-4" />
                              {n.viewsCount}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <Heart className="w-4 h-4" />
                              {n.likesCount}
                            </span>
                          </div>

                          <Link
                            to={`/noticias/${n.slug}`}
                            className="inline-flex items-center gap-2 text-amber-700 font-bold hover:text-amber-900 group/link transition-colors"
                          >
                            Ler mais
                            <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                          </Link>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              {totalPaginas > 1 && (
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => setPagina((p) => Math.max(1, p - 1))}
                    disabled={paginaAtual === 1}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Anterior
                  </button>

                  <div className="flex items-center gap-2">
                    {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        onClick={() => setPagina(p)}
                        className={`w-10 h-10 rounded-lg font-bold transition-all ${
                          p === paginaAtual
                            ? "bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg"
                            : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                    disabled={paginaAtual === totalPaginas}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Próxima
                  </button>
                </div>
              )}
            </>
          )}

          {!loading && !error && noticiasPaginadas.length === 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-semibold text-lg">
                Nenhuma notícia encontrada
              </p>
              <p className="text-gray-500 mt-2">Tente ajustar os filtros ou a busca</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Noticias;